import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { CheckCircle, ShieldCheck, SpinnerGap, Clock, CaretUp, CaretDown, ArrowsDownUp, X } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { formatDateTime } from '../lib/dateLocal';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const fmt = (n) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Number(n) || 0);

const PAYMENT_LABEL = {
    cash: 'Efectivo', credit_card: 'T. Crédito', debit_card: 'T. Débito',
    credit: 'T. Crédito', debit: 'T. Débito', transfer: 'Transferencia',
    qr: 'QR', mixed: 'Mixto', courtesy: 'Cortesía',
};

// Row key must be unique across simple sales and per-leg mixed rows
const rowKey = (r) => r.payment_id ? `${r.id}:${r.payment_id}` : r.id;

// Get local YYYY-MM-DD from an ISO timestamp (GMT-6 America/Mexico_City)
const localDateOf = (iso) => {
    if (!iso) return '';
    try {
        const d = new Date(iso);
        // Force GMT-6 display
        const opts = { timeZone: 'America/Mexico_City', year: 'numeric', month: '2-digit', day: '2-digit' };
        const parts = new Intl.DateTimeFormat('en-CA', opts).format(d); // en-CA gives YYYY-MM-DD
        return parts;
    } catch {
        return '';
    }
};

// Column definition for sorting
const COLUMNS_PENDING = [
    { key: 'created_at', label: 'Fecha', sortable: true, className: 'text-xs' },
    { key: 'customer_name', label: 'Cliente', sortable: true, className: 'text-xs' },
    { key: 'seller_name', label: 'Promotora', sortable: true, className: 'text-xs' },
    { key: 'leg_amount', label: 'Monto', sortable: true, className: 'text-xs text-right' },
    { key: 'payment_method', label: 'Forma de pago', sortable: true, className: 'text-xs' },
    { key: 'card_last4', label: 'Tarjeta', sortable: true, className: 'text-xs' },
    { key: 'tpv_name', label: 'TPV', sortable: true, className: 'text-xs' },
    { key: 'operation_number', label: 'Nº Operación', sortable: true, className: 'text-xs' },
    { key: null, label: 'Validado', sortable: false, className: 'text-xs text-center' },
];

const COLUMNS_VALIDATED = [
    { key: 'created_at', label: 'Fecha de venta', sortable: true, className: 'text-xs' },
    { key: 'payment_validated_at', label: 'Fecha de validación', sortable: true, className: 'text-xs' },
    { key: 'customer_name', label: 'Cliente', sortable: true, className: 'text-xs' },
    { key: 'seller_name', label: 'Promotora', sortable: true, className: 'text-xs' },
    { key: 'leg_amount', label: 'Monto', sortable: true, className: 'text-xs text-right' },
    { key: 'payment_method', label: 'Forma de pago', sortable: true, className: 'text-xs' },
    { key: 'card_last4', label: 'Tarjeta', sortable: true, className: 'text-xs' },
    { key: 'tpv_name', label: 'TPV', sortable: true, className: 'text-xs' },
    { key: 'operation_number', label: 'Nº Op.', sortable: true, className: 'text-xs' },
    { key: 'payment_validated_by_name', label: 'Validado por', sortable: true, className: 'text-xs' },
];

// Reusable sortable header cell
const SortableHead = ({ col, sortState, onSort, testid }) => {
    const active = sortState.key === col.key;
    const dir = sortState.dir;
    if (!col.sortable) {
        return <TableHead className={col.className}>{col.label}</TableHead>;
    }
    return (
        <TableHead className={col.className}>
            <button
                type="button"
                onClick={() => onSort(col.key)}
                data-testid={testid}
                className="inline-flex items-center gap-1 hover:text-[#FB7185] transition-colors font-inherit uppercase tracking-wide"
            >
                <span>{col.label}</span>
                {active
                    ? (dir === 'asc' ? <CaretUp size={10} weight="bold" /> : <CaretDown size={10} weight="bold" />)
                    : <ArrowsDownUp size={10} className="opacity-40" />}
            </button>
        </TableHead>
    );
};

// Compare helper: numeric fields as numbers, dates as strings (ISO is lexical-safe), rest as string
const compareBy = (a, b, key) => {
    const av = a?.[key];
    const bv = b?.[key];
    if (av == null && bv == null) return 0;
    if (av == null) return -1;
    if (bv == null) return 1;
    if (typeof av === 'number' && typeof bv === 'number') return av - bv;
    return String(av).localeCompare(String(bv), 'es', { numeric: true, sensitivity: 'base' });
};

const PaymentsValidationPage = () => {
    const { can, isAdmin } = useAuth();
    const canValidate = can('payments.validate_action') || can('sales.validate_payment');
    const canViewFull = isAdmin() || can('payments.validate_history_full');
    const canViewCurrent = isAdmin() || can('payments.validate_history_current_cut');
    const canViewValidated = canViewFull || canViewCurrent;

    const [pending, setPending] = useState([]);
    const [validated, setValidated] = useState([]);
    const [scope, setScope] = useState('current_cut'); // 'full' | 'current_cut'
    const [loading, setLoading] = useState(true);
    const [savingKey, setSavingKey] = useState(null);

    // ==== Shared filters (apply to BOTH tables) ====
    const [filters, setFilters] = useState({
        date_from: '',
        date_to: '',
        seller_id: 'all',
        payment_method: 'all',
        customer_search: '',
    });

    // ==== Sort state per table ====
    const [pendingSort, setPendingSort] = useState({ key: 'created_at', dir: 'desc' });
    const [validatedSort, setValidatedSort] = useState({ key: 'payment_validated_at', dir: 'desc' });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const p = axios.get(`${API}/payments/pending`, { headers: authHeaders() });
            const v = canViewValidated
                ? axios.get(`${API}/payments/validated?limit=${canViewFull ? 5000 : 1000}`, { headers: authHeaders() })
                : Promise.resolve({ data: { sales: [], scope: 'current_cut' } });
            const [pRes, vRes] = await Promise.all([p, v]);
            setPending(pRes.data.sales || []);
            setValidated(vRes.data.sales || []);
            setScope(vRes.data.scope || 'current_cut');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'No se pudieron cargar los pagos');
        } finally {
            setLoading(false);
        }
    }, [canViewValidated, canViewFull]);

    useEffect(() => { load(); }, [load]);

    const toggleValidate = async (row) => {
        if (!canValidate) {
            toast.error('No tienes permiso para validar pagos');
            return;
        }
        const key = rowKey(row);
        setSavingKey(key);
        try {
            const body = row.payment_id ? { payment_id: row.payment_id } : {};
            await axios.post(`${API}/sales/${row.id}/validate-payment`, body, { headers: authHeaders() });
            toast.success(row.is_mixed_leg
                ? `Porción validada: ${row.customer_name} — ${fmt(row.leg_amount)}`
                : `Pago validado: ${row.customer_name}`);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'No se pudo validar el pago');
        } finally {
            setSavingKey(null);
        }
    };

    // Unique seller list derived from both datasets (for the filter dropdown)
    const sellerOptions = useMemo(() => {
        const map = new Map();
        [...pending, ...validated].forEach(r => {
            const name = r.seller_name;
            if (name && name !== '—' && !map.has(name)) map.set(name, name);
        });
        return Array.from(map.keys()).sort((a, b) => a.localeCompare(b, 'es'));
    }, [pending, validated]);

    // Filter applier (shared between both tables)
    const applyFilters = useCallback((rows, dateKey) => {
        return rows.filter(r => {
            // Date range: use `dateKey` (created_at for pending, payment_validated_at for validated)
            const rowDate = localDateOf(r[dateKey] || r.created_at);
            if (filters.date_from && rowDate < filters.date_from) return false;
            if (filters.date_to && rowDate > filters.date_to) return false;
            // Seller
            if (filters.seller_id !== 'all' && r.seller_name !== filters.seller_id) return false;
            // Payment method — match both leg method and sale-level method
            if (filters.payment_method !== 'all') {
                const legMethod = r.payment_method || '';
                const saleMethod = r.sale_payment_method || legMethod;
                // "mixed" filter: any row from a mixed sale (leg or not)
                if (filters.payment_method === 'mixed') {
                    if (saleMethod !== 'mixed') return false;
                } else if (filters.payment_method === 'courtesy') {
                    if (!r.is_courtesy && legMethod !== 'courtesy' && saleMethod !== 'courtesy') return false;
                } else {
                    // For efectivo/tarjeta filters: match the LEG method (so a card leg of a mixed sale still shows under "credit_card")
                    if (legMethod !== filters.payment_method) return false;
                }
            }
            // Customer search (substring, case-insensitive)
            if (filters.customer_search.trim()) {
                const q = filters.customer_search.trim().toLowerCase();
                if (!(r.customer_name || '').toLowerCase().includes(q)) return false;
            }
            return true;
        });
    }, [filters]);

    // Sort applier
    const applySort = useCallback((rows, sortState) => {
        if (!sortState.key) return rows;
        const sorted = [...rows].sort((a, b) => compareBy(a, b, sortState.key));
        if (sortState.dir === 'desc') sorted.reverse();
        return sorted;
    }, []);

    const filteredPending = useMemo(
        () => applySort(applyFilters(pending, 'created_at'), pendingSort),
        [pending, applyFilters, applySort, pendingSort]
    );
    const filteredValidated = useMemo(
        () => applySort(applyFilters(validated, 'payment_validated_at'), validatedSort),
        [validated, applyFilters, applySort, validatedSort]
    );

    const handleSort = (setter, currentSort) => (colKey) => {
        if (!colKey) return;
        if (currentSort.key === colKey) {
            setter({ key: colKey, dir: currentSort.dir === 'asc' ? 'desc' : 'asc' });
        } else {
            setter({ key: colKey, dir: 'asc' });
        }
    };

    const clearFilters = () => setFilters({
        date_from: '', date_to: '', seller_id: 'all', payment_method: 'all', customer_search: ''
    });

    const activeFiltersCount = [
        filters.date_from, filters.date_to,
        filters.seller_id !== 'all' ? filters.seller_id : '',
        filters.payment_method !== 'all' ? filters.payment_method : '',
        filters.customer_search.trim(),
    ].filter(Boolean).length;

    if (loading) {
        return (
            <div className="flex items-center justify-center py-24 text-[#8B8474]" data-testid="payments-loading">
                <SpinnerGap size={24} className="animate-spin mr-2" /> Cargando pagos pendientes…
            </div>
        );
    }

    return (
        <div className="p-6 space-y-6" data-testid="payments-validation-page">
            <div>
                <h1 className="font-editorial text-3xl font-medium text-[#1F2D4B] flex items-center gap-2">
                    <ShieldCheck size={28} weight="duotone" className="text-[#FB7185]" /> Validación de Pagos
                </h1>
                <p className="text-sm text-[#4B5878] mt-1">
                    Confirma cada pago revisando con tu reporte del banco / TPV. Sólo los pagos validados pueden incluirse en un corte y generar comisión.
                    <br />
                    <span className="text-xs text-[#8B8474]">Los pagos mixtos se validan por porción: la parte en efectivo se valida automáticamente, la parte con tarjeta requiere validación manual.</span>
                </p>
            </div>

            {/* Filter toolbar — shared between both tables */}
            <Card className="bg-white border-[#ECE3D2] rounded-2xl">
                <CardContent className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end" data-testid="payments-filters">
                        <div>
                            <Label htmlFor="filter-date-from" className="text-xs text-[#8B8474]">Desde</Label>
                            <Input
                                id="filter-date-from"
                                type="date"
                                data-testid="filter-date-from"
                                value={filters.date_from}
                                onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                                className="mt-1 text-xs"
                            />
                        </div>
                        <div>
                            <Label htmlFor="filter-date-to" className="text-xs text-[#8B8474]">Hasta</Label>
                            <Input
                                id="filter-date-to"
                                type="date"
                                data-testid="filter-date-to"
                                value={filters.date_to}
                                onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                                className="mt-1 text-xs"
                            />
                        </div>
                        <div>
                            <Label className="text-xs text-[#8B8474]">Promotora</Label>
                            <Select
                                value={filters.seller_id}
                                onValueChange={(v) => setFilters({ ...filters, seller_id: v })}
                            >
                                <SelectTrigger data-testid="filter-seller" className="mt-1 text-xs h-9">
                                    <SelectValue placeholder="Todas" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Todas</SelectItem>
                                    {sellerOptions.map(name => (
                                        <SelectItem key={name} value={name}>{name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs text-[#8B8474]">Método de pago</Label>
                            <Select
                                value={filters.payment_method}
                                onValueChange={(v) => setFilters({ ...filters, payment_method: v })}
                            >
                                <SelectTrigger data-testid="filter-payment-method" className="mt-1 text-xs h-9">
                                    <SelectValue placeholder="Todos" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Todos</SelectItem>
                                    <SelectItem value="cash">Efectivo</SelectItem>
                                    <SelectItem value="credit_card">Tarjeta de crédito</SelectItem>
                                    <SelectItem value="debit_card">Tarjeta de débito</SelectItem>
                                    <SelectItem value="courtesy">Cortesía</SelectItem>
                                    <SelectItem value="mixed">Mixto</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label htmlFor="filter-customer" className="text-xs text-[#8B8474]">Cliente</Label>
                            <div className="flex items-center gap-2 mt-1">
                                <Input
                                    id="filter-customer"
                                    type="text"
                                    placeholder="Buscar…"
                                    data-testid="filter-customer"
                                    value={filters.customer_search}
                                    onChange={(e) => setFilters({ ...filters, customer_search: e.target.value })}
                                    className="text-xs"
                                />
                                {activeFiltersCount > 0 && (
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={clearFilters}
                                        data-testid="clear-filters"
                                        className="text-xs text-[#FB7185] hover:text-[#FB7185] hover:bg-[#FEE2E2] shrink-0"
                                        title="Limpiar filtros"
                                    >
                                        <X size={14} />
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                    {activeFiltersCount > 0 && (
                        <div className="text-[10px] text-[#8B8474] mt-2" data-testid="filters-active-count">
                            {activeFiltersCount} filtro{activeFiltersCount === 1 ? '' : 's'} activo{activeFiltersCount === 1 ? '' : 's'}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Pending */}
            <Card className="bg-white border-[#ECE3D2] rounded-2xl overflow-hidden">
                <CardContent className="p-0">
                    <div className="flex items-center justify-between px-5 py-3 bg-[#F8F4EC] border-b border-[#ECE3D2]">
                        <h2 className="font-display text-base font-semibold text-[#1F2D4B] flex items-center gap-2">
                            <Clock size={18} weight="duotone" className="text-[#FB7185]" />
                            Pendientes de validar
                            <span className="text-xs font-mono text-[#8B8474]">
                                ({filteredPending.length}{filteredPending.length !== pending.length ? ` de ${pending.length}` : ''})
                            </span>
                        </h2>
                    </div>
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-white hover:bg-white border-b border-[#ECE3D2]">
                                    {COLUMNS_PENDING.map((col, i) => (
                                        <SortableHead
                                            key={col.key || `no-${i}`}
                                            col={col}
                                            sortState={pendingSort}
                                            onSort={handleSort(setPendingSort, pendingSort)}
                                            testid={col.key ? `pending-sort-${col.key}` : undefined}
                                        />
                                    ))}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredPending.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={9} className="text-center py-10 text-[#8B8474]" data-testid="empty-pending">
                                            <CheckCircle size={32} weight="duotone" className="mx-auto mb-2 text-[#65C76A]" />
                                            <p className="font-medium">
                                                {pending.length === 0 ? 'No hay pagos pendientes de validar' : 'Ningún pago coincide con los filtros'}
                                            </p>
                                        </TableCell>
                                    </TableRow>
                                ) : filteredPending.map((s) => {
                                    const k = rowKey(s);
                                    const displayAmount = s.is_mixed_leg ? s.leg_amount : s.total_amount;
                                    return (
                                        <TableRow key={k} data-testid={`pending-row-${k}`}>
                                            <TableCell className="text-xs whitespace-nowrap">{formatDateTime(s.created_at)}</TableCell>
                                            <TableCell className="text-sm font-medium text-[#1F2D4B]">
                                                {s.customer_name}
                                                {s.is_mixed_leg && (
                                                    <div className="text-[10px] text-[#8B8474] font-normal mt-0.5">
                                                        Venta mixta · Total {fmt(s.total_amount)}
                                                    </div>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-xs text-[#4B5878]">{s.seller_name}</TableCell>
                                            <TableCell className="text-sm text-right font-semibold text-[#1F2D4B]">
                                                {fmt(displayAmount)}
                                            </TableCell>
                                            <TableCell className="text-xs uppercase tracking-wider text-[#8B8474]">
                                                <div className="flex items-center gap-1.5">
                                                    {s.is_mixed_leg && (
                                                        <span className="text-[9px] bg-[#FB7185]/10 text-[#FB7185] px-1.5 py-0.5 rounded font-semibold" data-testid={`mixed-badge-${k}`}>
                                                            MIXTO
                                                        </span>
                                                    )}
                                                    <span>{PAYMENT_LABEL[s.payment_method] || s.payment_method}</span>
                                                </div>
                                            </TableCell>
                                            <TableCell className="font-mono text-xs">{s.card_last4 ? `•••• ${s.card_last4}` : '—'}</TableCell>
                                            <TableCell className="font-mono text-xs text-[#4B5878]">{s.tpv_name || s.tpv_id || '—'}</TableCell>
                                            <TableCell className="font-mono text-xs text-[#4B5878]">{s.operation_number || '—'}</TableCell>
                                            <TableCell className="text-center">
                                                <button
                                                    onClick={() => toggleValidate(s)}
                                                    disabled={!canValidate || savingKey === k}
                                                    data-testid={`validate-checkbox-${k}`}
                                                    className="inline-flex items-center justify-center w-6 h-6 rounded border-2 border-[#1F2D4B]/40 hover:border-[#65C76A] hover:bg-[#ECFDF5] disabled:opacity-50 transition-colors"
                                                    title={canValidate ? 'Marcar como validado' : 'Sin permiso para validar'}
                                                >
                                                    {savingKey === k ? <SpinnerGap size={14} className="animate-spin" /> : null}
                                                </button>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>

            {/* Validated history */}
            {canViewValidated && (
                <Card className="bg-white border-[#ECE3D2] rounded-2xl overflow-hidden">
                    <CardContent className="p-0">
                        <div className="flex items-center justify-between px-5 py-3 bg-[#F8F4EC] border-b border-[#ECE3D2]">
                            <h2 className="font-display text-base font-semibold text-[#1F2D4B] flex items-center gap-2">
                                <CheckCircle size={18} weight="duotone" className="text-[#65C76A]" />
                                Validados
                                <span className="text-xs font-mono text-[#8B8474]">
                                    ({filteredValidated.length}{filteredValidated.length !== validated.length ? ` de ${validated.length}` : ''})
                                </span>
                                <span
                                    className="text-[9px] uppercase tracking-wide bg-[#1F2D4B]/10 text-[#1F2D4B] px-2 py-0.5 rounded-full font-semibold"
                                    data-testid="validated-scope-badge"
                                    title={scope === 'full'
                                        ? 'Muestra el histórico completo, incluyendo ventas ya cortadas.'
                                        : 'Muestra únicamente pagos validados del corte actual (aún no cerrados).'}
                                >
                                    {scope === 'full' ? 'Histórico completo' : 'Corte actual'}
                                </span>
                            </h2>
                        </div>
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-white hover:bg-white border-b border-[#ECE3D2]">
                                        {COLUMNS_VALIDATED.map((col, i) => (
                                            <SortableHead
                                                key={col.key || `no-${i}`}
                                                col={col}
                                                sortState={validatedSort}
                                                onSort={handleSort(setValidatedSort, validatedSort)}
                                                testid={col.key ? `validated-sort-${col.key}` : undefined}
                                            />
                                        ))}
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredValidated.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={10} className="text-center py-6 text-xs text-[#8B8474] italic" data-testid="empty-validated">
                                                {validated.length === 0
                                                    ? 'Aún no hay pagos validados.'
                                                    : 'Ningún pago validado coincide con los filtros.'}
                                            </TableCell>
                                        </TableRow>
                                    ) : filteredValidated.map((s) => {
                                        const k = rowKey(s);
                                        const displayAmount = s.is_mixed_leg ? s.leg_amount : s.total_amount;
                                        const isSystem = (s.payment_validated_by_name || '').toLowerCase().startsWith('sistema');
                                        return (
                                            <TableRow key={k} data-testid={`validated-row-${k}`}>
                                                <TableCell className="text-xs whitespace-nowrap" data-testid={`validated-sale-date-${k}`}>{formatDateTime(s.created_at)}</TableCell>
                                                <TableCell className="text-xs whitespace-nowrap" data-testid={`validated-validation-date-${k}`}>{formatDateTime(s.payment_validated_at || s.created_at)}</TableCell>
                                                <TableCell className="text-sm font-medium text-[#1F2D4B]">
                                                    {s.customer_name}
                                                    {s.is_mixed_leg && (
                                                        <div className="text-[10px] text-[#8B8474] font-normal mt-0.5">
                                                            Venta mixta · Total {fmt(s.total_amount)}
                                                        </div>
                                                    )}
                                                </TableCell>
                                                <TableCell className="text-xs text-[#4B5878]">{s.seller_name || '—'}</TableCell>
                                                <TableCell className="text-sm text-right text-[#1F2D4B]">{fmt(displayAmount)}</TableCell>
                                                <TableCell className="text-xs uppercase tracking-wider text-[#8B8474]">
                                                    <div className="flex items-center gap-1.5">
                                                        {s.is_mixed_leg && (
                                                            <span className="text-[9px] bg-[#FB7185]/10 text-[#FB7185] px-1.5 py-0.5 rounded font-semibold">
                                                                MIXTO
                                                            </span>
                                                        )}
                                                        <span>{PAYMENT_LABEL[s.payment_method] || s.payment_method}</span>
                                                    </div>
                                                </TableCell>
                                                <TableCell className="font-mono text-xs">{s.card_last4 ? `•••• ${s.card_last4}` : '—'}</TableCell>
                                                <TableCell className="font-mono text-xs text-[#4B5878]">{s.tpv_name || s.tpv_id || '—'}</TableCell>
                                                <TableCell className="font-mono text-xs text-[#4B5878]">{s.operation_number || '—'}</TableCell>
                                                <TableCell className="text-xs">
                                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-medium ${isSystem ? 'bg-[#EEF2FF] text-[#3730A3]' : 'bg-[#ECFDF5] text-[#065F46]'}`}>
                                                        <CheckCircle size={12} weight="bold" />
                                                        {isSystem ? s.payment_validated_by_name : `por ${s.payment_validated_by_name || '—'}`}
                                                    </span>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
};

export default PaymentsValidationPage;
